import { Type } from 'src/components/base/MessagePane';
import office from 'src/services/office';

import { isPullKind, isInsertKind, isDeleteKind, toVerbForms, ErrorReaction, NoDataError, TruncateDataError, ResultDataError, FlowstepType } from 'shared/utils/flowstep';
import { capitalizeString, MultipleErrorsError } from 'shared/utils/utils';
import { CancelError } from 'shared/utils/cancelToken';

import { DeleteFlowstep } from 'src/services/runtime/deleteFlowstep';
import { InsertFlowstep } from 'src/services/runtime/insertFlowstep';
import { PullFlowstep } from 'src/services/runtime/pullFlowstep';
import { ConvertLeads } from 'src/services/runtime/convertLeads';
import { MergeObjects } from 'src/services/runtime/mergeObjects';

import { selectRange } from 'src/services/runtime/runnable';

import { officeStore, contextStore, dataStore } from 'src/stores';

const Button = {
  Stop: { key: 'stop', text: 'No' },
  Ignore: { key: 'ignore', text: 'Yes', primary: true },
  Cancel: { key: 'cancel', text: 'Cancel' },
  Create: { key: 'create', text: 'Yes', primary: true },
};

export const StopReason = {
  Normal: 'normal',
  ErrorReaction: 'error-reaction',
  User: 'user',
};

export const Data = {
  BorderFormat: { weight: 'Thin', color: '#000000' },
  buttons: [Button.Stop, Button.Ignore],
  flowstepButtons: [Button.Cancel, Button.Create],
  maxErrorsToDisplay: 10,
  messagePaneTimeout: 10 * 1000, // in ms
  successText: 'Successfully executed',
  dataErrorsText: 'Errors were encountered',
};


export async function execute(flowsteps, meta, parentRunnable = null) {
  const worksheetsToReload = new Set();
  const { requestStore } = officeStore;
  const worksheets = await office.getAllWorksheets();

  const result = { flowsteps: [], stopReason: StopReason.Normal };

  for (let i = 0; i < flowsteps.length; ++i) {
    const flowstep = flowsteps[i];
    const { worksheetId } = flowstep;

    if (!worksheets.find(({ id }) => id === worksheetId)) {
      const text = `This flow step cannot be run because it’s not associated with any worksheet. Would you like to associate it with the active sheet (you may need to revisit mappings)`
      const key = await requestStore.showDialog(text, Data.flowstepButtons);
      if (key === Button.Cancel.key) {
        result.stopReason = StopReason.User;
        continue
      }
      else {
        const activeWorksheet = await office.getActiveWorksheet()
        flowstep.worksheetId = activeWorksheet.id;
        await dataStore.createOrUpdateFlowstep(flowstep)
      }
    }

    const executor = createExecutor(flowstep, meta, parentRunnable);
    const executionResult = await executor.run();
    if (flowstep.options.recalculateFormulas) {
      await office.calculateFormulas();
    }
    const { errorReaction: reaction } = flowstep.options;
    const { error, warnings } = executionResult;
    result.flowsteps.push({ executor, result: executionResult });
    worksheetsToReload.add(worksheetId);

    const hasDataErrors = !!warnings.find(x => x instanceof ResultDataError);

    if (!error && !hasDataErrors) { continue; }
    if (error && error instanceof CancelError) { throw error; }

    worksheetsToReload.delete(worksheetId);
    if (reaction === ErrorReaction.Continue) { continue; }

    if (reaction === ErrorReaction.Stop) {
      result.stopReason = StopReason.ErrorReaction;
      break;
    }

    // ErrorReaction.AskUser
    if (i < flowsteps.length - 1) {
      const text = `Error happened while executing flow step '${flowstep.name}'. Do you want to ignore it and continue execution?`
      const key = await executor.requestStore.showDialog(text, Data.buttons);
      if (key === Button.Stop.key) {
        result.stopReason = StopReason.User;
        break;
      }
    }
    else {
      result.stopReason = StopReason.ErrorReaction;
      break;
    }
  }

  requestStore.setText('Reloading context...');
  await contextStore.reloadWorksheetContext([...worksheetsToReload]);

  return result;
}


export function createExecutor(flowstep, meta, parent) {
  const create = () => {
    const { type } = flowstep;
    if (type === FlowstepType.ConvertLeads) { return new ConvertLeads(flowstep, meta, parent); }
    if (type === FlowstepType.MergeObjects) { return new MergeObjects(flowstep, meta, parent); }
    if (isInsertKind(type)) { return new InsertFlowstep(flowstep, meta, parent); }
    if (isDeleteKind(type)) { return new DeleteFlowstep(flowstep, meta, parent); }
    if (isPullKind(type)) { return new PullFlowstep(flowstep, meta, parent); }
    throw new Error(`Unsupported flow step type '${type}'`);
  }

  const executor = create();
  const { isPull } = executor;
  const { requestStore } = officeStore;

  const setText = (text, description) => requestStore.setText(text, { description, withSpinner: true });
  const showPane = (params, logRange = null, errorsRange = null) => {
    errorsRange?.track();
    logRange?.track();

    requestStore.showPane({
      buttons: [
        errorsRange && { text: 'Go to errors', onClick: async () => await selectRange(errorsRange) },
        logRange && { text: 'Go to log', onClick: async () => await selectRange(logRange) },
      ].filter(Boolean),
      ...params,
    })
  }

  executor
    .on('started', () => {
      executor.logger.info('Execution started');
      setText('Detecting range');
    })
    .on('precollecting', () => setText(isPull ? 'Clearing filters' : 'Preparing table for data collection'))
    .on('collecting', () => setText('Collecting worksheet data'))
    .on('preprocessing', () => setText(isPull ? 'Preparing query' : 'Preparing data'))
    .on('remote-starting', () => setText('Uploading records to Salesforce'))
    .on('remote-waiting', () => setText('Preparing table for results'))
    .on('remote-receiving', () => setText('Retrieving results'))
    .on('processing-chunk', (executor) => {
      const status = isPull
        ? `Populating spreadsheet`
        : `Populating results`;
      const description = isPull
        ? `${executor.processedRowCount} rows added`
        : `${executor.processedRowCount} of ${executor.rowToProcessCount}`;
      setText(capitalizeString(status), description);
    })
    .on('postprocessing', () => setText('Restoring filters'))
    .on('clearing', () => setText('Deleting unused data')) // Pull-specific
    .on('applying-validation', () => setText('Applying data validation')) // Pull-specific
    .on('creating-table', () => setText('Creating table')) // Pull-specific
    .on('creating-new-worksheet', (executor, worksheetName) => executor.logger.info(`Creating new worksheet '${worksheetName}'`)) // Pull-specific
    .on('inserting-dateTime-column', () => setText('Appending DateTime column')) // Pull-specific
    .on('finished', (executor, result) => {
      const { logger } = executor;
      const { warnings } = result;

      function displayWarnings() {
        function displayMessage(logErrorMessages, paneMessage, errorRange, severity = Type.Info) {
          const logErrorRange = logger.info(logErrorMessages);
          showPane({ message: paneMessage, type: severity, }, logErrorRange, errorRange);
        }

        const warning = warnings[0];
        if (warning instanceof NoDataError) {
          const { message } = warning;
          displayMessage(message, message, null);
        }
        if (warning instanceof TruncateDataError) {
          const { message } = warning;
          displayMessage(message, message, null, Type.Warning);
        }
        // ResultDataError warning is an error now 
        // else if (warning instanceof ResultDataError) {
        //   const { errors, range } = warning;
        //   const paneMessage = `Errors during execution of flow step '${flowstep.name}' were encountered`;
        //   displayWarning([...errors, 'Errors were encountered'], paneMessage, range);
        // }
      }

      if (warnings.length) {
        displayWarnings();
        return;
      }

      const { resultSuccessRange } = executor;
      resultSuccessRange?.track();

      showPane({
        message: `Flow step '${flowstep.name}' was successfully executed`,
        buttons: resultSuccessRange
          ? [{ text: `Show ${isPull ? 'data' : 'results'}`, onClick: async () => await selectRange(resultSuccessRange) }]
          : [],
        type: Type.Success,
        timeout: Data.messagePaneTimeout,
      });

      logger.info(Data.successText);
    }
    )
    .on('error', (executor, error) => {
      const { logger } = executor;

      if (error instanceof CancelError) {
        logger.warn(`Execution was terminated by user`);
        requestStore.warn(`Execution of flow step '${flowstep.name}' was terminated by user`, Data.messagePaneTimeout);
        return;
      }

      let logErrorRange = null;
      let errorsRange = null;

      if (error instanceof MultipleErrorsError) {
        const { errors } = error;

        logErrorRange = logger.error(errors);
        const trimmed = errors.slice(0, Math.min(Data.maxErrorsToDisplay, errors.length));

        if (error instanceof ResultDataError) {
          errorsRange = error.range;
          error.message = 'Errors were encountered'
        }
        else {
          error.message = 'Fix the following errors to continue';
        }

        error.message = `${error.message} (${trimmed.length} of ${errors.length} shown):\n`
          + trimmed.map((x, index) => `${index + 1}. ${x}`).join('\n');
      }
      else {
        logErrorRange = logger.error(error.message);
      }

      logger.error(`Unable to execute flow step`);

      // --- DEBUG ---
      // error.message = `${flowstep.name} - ${error.message}`;
      // console.error(error.message);
      // console.error(error.stack);
      // throw error;

      showPane({ message: `${flowstep.name} - ${error.message}`, type: Type.Error }, logErrorRange, errorsRange);
    });

  return executor;
}


